/**
 * BIP-39 Mnemonic Implementation
 *
 * Provides seed ↔ mnemonic conversion using the BIP-39 standard.
 * Must be compatible with Go's github.com/tyler-smith/go-bip39
 */

import * as bip39 from 'bip39';
import { CryptoError } from './types';
import { bytesToHex, hexToBytes } from './utils';

/**
 * Normalize a mnemonic string for consistent handling
 *
 * Handles common input variations:
 * - Newlines (from pasting multi-line text)
 * - Multiple spaces (from copy/paste artifacts)
 * - Leading/trailing whitespace
 * - Mixed case
 */
function normalizeMnemonic(mnemonic: string): string {
  return mnemonic
    .replace(/[\r\n]+/g, ' ')  // newlines → spaces
    .replace(/\s+/g, ' ')       // collapse multiple spaces
    .trim()
    .toLowerCase();
}

/**
 * Convert a seed (entropy) to BIP-39 mnemonic words
 *
 * Matches Go: crypto.AsPaperKey(seed)
 * Go reference: github.com/tyler-smith/go-bip39 NewMnemonic()
 *
 * @param seed - Seed bytes (must be 16, 20, 24, 28, or 32 bytes)
 * @returns Array of mnemonic words
 * @throws CryptoError if seed length is invalid
 */
export function seedToMnemonic(seed: Uint8Array): string[] {
  // Validate seed length (BIP-39 requires specific lengths)
  const validLengths = [16, 20, 24, 28, 32];
  if (!validLengths.includes(seed.length)) {
    throw new CryptoError(
      `Invalid seed length: ${seed.length}. Must be one of: ${validLengths.join(', ')}`,
      'INVALID_SEED_LENGTH'
    );
  }

  try {
    // Convert Uint8Array to hex string for bip39 library
    const hexSeed = bytesToHex(seed);

    // Generate mnemonic from hex entropy
    const mnemonicString = bip39.entropyToMnemonic(hexSeed);

    // Return as array of words
    return mnemonicString.split(' ');
  } catch (error) {
    throw new CryptoError(
      `Failed to generate mnemonic: ${error instanceof Error ? error.message : String(error)}`,
      'MNEMONIC_GENERATION_FAILED'
    );
  }
}

/**
 * Convert BIP-39 mnemonic words to seed (entropy)
 *
 * Matches Go: crypto.AsSymmetricKey(mnemonic)
 * Go reference: github.com/tyler-smith/go-bip39 EntropyFromMnemonic()
 *
 * @param mnemonic - Array of mnemonic words or space-separated string
 * @returns Seed bytes
 * @throws CryptoError if mnemonic is invalid
 */
export function mnemonicToSeed(mnemonic: string[] | string): Uint8Array {
  try {
    // Convert array to space-separated string if needed, then normalize
    const mnemonicString = normalizeMnemonic(
      Array.isArray(mnemonic) ? mnemonic.join(' ') : mnemonic
    );

    // Validate mnemonic
    if (!bip39.validateMnemonic(mnemonicString)) {
      throw new CryptoError('Invalid mnemonic phrase', 'INVALID_MNEMONIC');
    }

    // Convert mnemonic to entropy (seed)
    const hexSeed = bip39.mnemonicToEntropy(mnemonicString);

    // Convert hex string to Uint8Array
    return hexToBytes(hexSeed);
  } catch (error) {
    if (error instanceof CryptoError) {
      throw error;
    }
    throw new CryptoError(
      `Failed to parse mnemonic: ${error instanceof Error ? error.message : String(error)}`,
      'MNEMONIC_PARSE_FAILED'
    );
  }
}

/**
 * Validate a BIP-39 mnemonic phrase
 *
 * @param mnemonic - Array of mnemonic words or space-separated string
 * @returns True if valid, false otherwise
 */
export function validateMnemonic(mnemonic: string[] | string): boolean {
  try {
    const mnemonicString = normalizeMnemonic(
      Array.isArray(mnemonic) ? mnemonic.join(' ') : mnemonic
    );
    return bip39.validateMnemonic(mnemonicString);
  } catch {
    return false;
  }
}

/**
 * Generate a random BIP-39 mnemonic phrase
 *
 * @param strength - Entropy strength in bits (128, 160, 192, 224, or 256). Default: 256 (24 words)
 * @returns Array of mnemonic words
 * @throws CryptoError if strength is invalid
 */
export function generateMnemonic(strength: number = 256): string[] {
  const validStrengths = [128, 160, 192, 224, 256];
  if (!validStrengths.includes(strength)) {
    throw new CryptoError(
      `Invalid strength: ${strength}. Must be one of: ${validStrengths.join(', ')}`,
      'INVALID_STRENGTH'
    );
  }

  try {
    const mnemonicString = bip39.generateMnemonic(strength);
    return mnemonicString.split(' ');
  } catch (error) {
    throw new CryptoError(
      `Failed to generate mnemonic: ${error instanceof Error ? error.message : String(error)}`,
      'MNEMONIC_GENERATION_FAILED'
    );
  }
}

/**
 * Get the number of words for a given entropy strength
 *
 * @param strength - Entropy strength in bits
 * @returns Number of mnemonic words
 */
export function getWordCount(strength: number): number {
  // BIP-39: word count = entropy bits / 32 * 3
  return (strength / 32) * 3;
}

/**
 * Get the entropy strength for a given number of words
 *
 * @param wordCount - Number of mnemonic words
 * @returns Entropy strength in bits
 */
export function getStrength(wordCount: number): number {
  // BIP-39: entropy bits = word count / 3 * 32
  return (wordCount / 3) * 32;
}
